import { useEffect } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Every element some open layer has hidden: how many layers hold it, and its attributes from before the first one.
// Layers can close in any order (browser Back unmounts the panel before the dialog over it), so an element gets its
// original values back only when the last holder lets go, and a holder never records another layer's hiding.
const holders = new Map();

function hold(el) {
  const entry = holders.get(el);
  if (entry) {
    entry.count += 1;
    return;
  }
  holders.set(el, { count: 1, inert: el.hasAttribute("inert"), ariaHidden: el.getAttribute("aria-hidden") });
  el.setAttribute("inert", "");
  el.setAttribute("aria-hidden", "true");
}

function release(el) {
  const entry = holders.get(el);
  if (!entry) return;
  entry.count -= 1;
  if (entry.count > 0) return;
  holders.delete(el);
  if (!entry.inert) el.removeAttribute("inert");
  if (entry.ariaHidden === null) el.removeAttribute("aria-hidden");
  else el.setAttribute("aria-hidden", entry.ariaHidden);
}

// Hides everything outside node: every sibling of node and of each ancestor up to body. Live regions (the toast)
// stay live unless hideLiveRegions; for that to work the toast region must be a sibling of one of the layer's
// ancestors (it is, as a child of .md-root next to the layers), never inside the layer.
function hideOutside(node, hideLiveRegions) {
  const held = [];
  for (let el = node; el && el.parentNode && el !== document.body; el = el.parentNode) {
    Array.from(el.parentNode.children).forEach((sibling) => {
      if (sibling === el || /^(SCRIPT|STYLE)$/.test(sibling.tagName)) return;
      if (!hideLiveRegions && sibling.hasAttribute("aria-live")) return;
      hold(sibling);
      held.push(sibling);
    });
  }
  return () => held.forEach(release);
}

const isFocusable = (el) => el.matches(FOCUSABLE) || el.hasAttribute("tabindex");
const targetOf = (target) => (target && "current" in target ? target.current : target);
const attached = (el) => !!el && el !== document.body && document.body.contains(el);

// Where focus goes on close: the element that had it at open, else the explicit target, else the nearest
// ancestor of the opener that is still mounted and focusable, else the main region. Never silently body.
function focusBack(previous, ancestors, returnFocusTo) {
  const explicit = targetOf(returnFocusTo);
  let target = [previous, explicit].find(attached) || ancestors.find((el) => attached(el) && isFocusable(el));
  if (!target) {
    target = document.querySelector('main, [role="main"]');
    if (target && !target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
  }
  if (target && target.focus) target.focus();
}

// While active: Tab stays inside ref (also from a tabindex=-1 heading), the rest of the app is inert and hidden,
// and on close focus goes back (see focusBack). A layer whose overlay must stay clickable puts ref on a wrapper
// around the overlay and the layer. Options: returnFocusTo (element or ref) for an opener that may unmount first;
// hideLiveRegions for a modal alertdialog, so a toast behind it cannot be clicked.
export default function useFocusTrap(ref, active, { returnFocusTo = null, hideLiveRegions = false } = {}) {
  useEffect(() => {
    if (!active || !ref.current) return undefined;
    const node = ref.current;
    const previous = document.activeElement;
    const ancestors = [];
    for (let el = previous && previous.parentElement; el && el !== document.body; el = el.parentElement) {
      ancestors.push(el);
    }
    const restore = hideOutside(node, hideLiveRegions);
    const onKeyDown = (event) => {
      // An inner layer (a dialog over the panel) already handled this Tab.
      if (event.key !== "Tab" || event.defaultPrevented) return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE));
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const index = items.indexOf(document.activeElement);
      if (event.shiftKey && index <= 0) {
        event.preventDefault();
        items[items.length - 1].focus();
      } else if (!event.shiftKey && (index === -1 || index === items.length - 1)) {
        event.preventDefault();
        items[0].focus();
      }
    };
    node.addEventListener("keydown", onKeyDown);
    return () => {
      node.removeEventListener("keydown", onKeyDown);
      // Move focus only if it is still in this layer or lost; if another open layer has it, leave it there.
      const owner = document.activeElement;
      const moveFocus = node.contains(owner) || !attached(owner);
      restore();
      if (moveFocus) focusBack(previous, ancestors, returnFocusTo);
    };
    // returnFocusTo and hideLiveRegions are read once per opening, like the opener itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, active]);
}
